// Google Analytics event tracking utilities

// Helper to check if analytics consent is given
const hasAnalyticsConsent = (): boolean => {
  const consent = localStorage.getItem('cookieConsent');
  if (!consent) return true; // Default to true if not set
  try {
    const consentData = JSON.parse(consent);
    return consentData.analytics !== false;
  } catch {
    return true;
  }
};

// Generate a session ID for tracking user sessions
let sessionId: string | null = null;
const getSessionId = (): string => {
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
  return sessionId;
};

// Get device category
const getDeviceCategory = (): string => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Get user agent category
const getUserAgentCategory = (): string => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('chrome')) return 'chrome';
  if (ua.includes('firefox')) return 'firefox';
  if (ua.includes('safari')) return 'safari';
  if (ua.includes('edge')) return 'edge';
  return 'other';
};

// Enhanced event tracking with validation and common parameters
export const trackEvent = (
  eventName: string,
  parameters?: {
    category?: string;
    label?: string;
    value?: number;
    [key: string]: any;
  }
) => {
  // Validate and check consent
  if (!eventName || !hasAnalyticsConsent()) return;
  
  // Add common parameters
  const enhancedParams = {
    ...parameters,
    timestamp: new Date().toISOString(),
    session_id: getSessionId(),
    device_category: getDeviceCategory(),
    user_agent_category: getUserAgentCategory(),
    page_location: window.location.pathname,
    page_title: document.title
  };
  
  // Send to GA4
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, enhancedParams);
  }
  
  // Log in development
  if (process.env.NODE_ENV === 'development') {
    console.log('[Analytics]', eventName, enhancedParams);
  }
};

// Initialize analytics with enhanced configuration
export const initializeAnalytics = () => {
  if (typeof window === 'undefined' || !window.gtag) return;
  
  // Set user properties
  window.gtag('set', 'user_properties', {
    app_version: process.env.REACT_APP_VERSION || '1.0.0',
    device_category: getDeviceCategory(),
    audio_api_supported: 'AudioContext' in window || 'webkitAudioContext' in window,
    language: navigator.language
  });
  
  // Configure enhanced measurement
  window.gtag('config', 'G-31VQMDJ61Q', {
    send_page_view: false, // We'll handle this manually
    cookie_flags: 'SameSite=None;Secure'
  });
};

// Predefined events for consistent tracking
export const analyticsEvents = {
  // Assessment events
  startAssessment: () => trackEvent('begin_assessment', {
    category: 'engagement',
    label: 'frequency_detection'
  }),
  
  completeAssessment: (frequency: number, iterations: number) => trackEvent('complete_assessment', {
    category: 'engagement',
    label: 'frequency_detection',
    value: frequency,
    iterations: iterations
  }),
  
  // Notched white noise events
  playNotchedNoise: (frequency: number) => trackEvent('play_notched_noise', {
    category: 'engagement',
    label: 'therapy',
    value: frequency
  }),
  
  // Therapy milestone tracking
  therapyMilestone: (durationMinutes: number, frequency: number, milestoneLabel: string) => trackEvent('therapy_milestone', {
    category: 'engagement',
    label: 'therapy_duration',
    value: frequency,
    session_duration_minutes: durationMinutes,
    milestone_type: milestoneLabel,
    therapy_type: 'notched_white_noise'
  }),
  
  // Sharing events
  shareFrequency: (method: string) => trackEvent('share', {
    category: 'engagement',
    method: method,
    content_type: 'frequency'
  }),
  
  // Navigation events
  viewPage: (pageName: string) => trackEvent('page_view', {
    page_title: pageName,
    page_location: window.location.href
  }),
  
  // User actions
  adjustVolume: (volume: number) => trackEvent('adjust_volume', {
    category: 'interaction',
    value: Math.round(volume * 100)
  }),
  
  pauseAssessment: () => trackEvent('pause_assessment', {
    category: 'engagement'
  }),
  
  resumeAssessment: () => trackEvent('resume_assessment', {
    category: 'engagement'
  }),
  
  // Error tracking
  trackError: (error: string, fatal: boolean = false) => trackEvent('exception', {
    description: error,
    fatal: fatal
  }),
  
  // Performance tracking
  trackPerformance: (metric: string, value: number) => trackEvent('performance', {
    category: 'technical',
    metric_name: metric,
    value: value
  }),
  
  // Feature interaction tracking
  trackFeatureInteraction: (featureName: string, interactionType: string, value?: any) => trackEvent('feature_interaction', {
    category: 'engagement',
    feature_name: featureName,
    interaction_type: interactionType,
    value: value
  }),

  // Visualization tracking
  trackVisualizationInteraction: (action: string, frequency: number) => trackEvent('visualization_interaction', {
    category: 'engagement',
    action: action,
    frequency: frequency,
    component: 'notch_filter'
  })
};

// Scroll depth tracking
let scrollDepthTracked = new Set<number>();
export const trackScrollDepth = () => {
  const scrollPercentage = Math.round(
    (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100
  );
  
  const milestones = [25, 50, 75, 100];
  milestones.forEach(milestone => {
    if (scrollPercentage >= milestone && !scrollDepthTracked.has(milestone)) {
      scrollDepthTracked.add(milestone);
      trackEvent('scroll_depth', {
        category: 'engagement',
        percentage: milestone,
        page_location: window.location.pathname
      });
    }
  });
};

// Engagement time tracking
let engagementStartTime = Date.now();
let engagementMilestones = new Set<number>();
export const trackEngagementTime = () => {
  const engagementSeconds = Math.floor((Date.now() - engagementStartTime) / 1000);
  const milestones = [30, 60, 180, 300]; // 30s, 1m, 3m, 5m
  
  milestones.forEach(milestone => {
    if (engagementSeconds >= milestone && !engagementMilestones.has(milestone)) {
      engagementMilestones.add(milestone);
      trackEvent('engagement_time', {
        category: 'engagement',
        duration_seconds: milestone,
        page_location: window.location.pathname
      });
    }
  });
};

// Reset tracking for new pages
export const resetPageTracking = () => {
  scrollDepthTracked.clear();
  engagementStartTime = Date.now();
  engagementMilestones.clear();
};

// Web Vitals tracking
export const trackWebVitals = (metric: { name: string; value: number }) => {
  trackEvent('web_vitals', {
    category: 'performance',
    metric_name: metric.name,
    value: Math.round(metric.value)
  });
};

// User timing tracking
export const trackTiming = (category: string, variable: string, value: number) => {
  trackEvent('timing_complete', {
    category: category,
    name: variable,
    value: Math.round(value)
  });
};